import React, { useEffect } from 'react';

import { Loader2 } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useRoutePermissions } from '@/modules/authorization/hooks/use-route-permissions';
import { Routes } from '@/router/routes';
import { logger } from '@/utils/logger/logger';

const TAG = 'RoutePermissionGuard:';

interface RoutePermissionGuardProps {
  children: React.ReactNode;
}

/**
 * Guard component that checks if user has permission to access the current route
 * If not, redirects to an accessible route
 */
export const RoutePermissionGuard: React.FC<RoutePermissionGuardProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { canAccessRoute, accessibleRedirectRoute, isLoading } = useRoutePermissions();

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const currentRoute = location.pathname as Routes;
    const hasAccess = canAccessRoute(currentRoute);

    if (!hasAccess) {
      logger.warn(TAG, `User does not have access to route: ${currentRoute}`);
      const fallbackRoute = accessibleRedirectRoute;
      logger.info(TAG, `Redirecting to accessible route: ${fallbackRoute}`);
      navigate(fallbackRoute, { replace: true });
    }
  }, [location.pathname, canAccessRoute, accessibleRedirectRoute, navigate, isLoading]);

  if (isLoading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return <>{children}</>;
};
